// Add a small buffer to prevent edge cases
const BUFFER = 5;

export const isOutOfViewport = (
  element: HTMLElement | null,
  targetElement?: HTMLElement | null,
) => {
  if (!element) {
    return { top: false, bottom: false, left: false, right: false };
  }

  const contentRect = element.getBoundingClientRect();

  // Check window viewport boundaries with buffer - this is the primary check
  const viewportResult = {
    top: contentRect.top < BUFFER,
    bottom: contentRect.bottom > window.innerHeight - BUFFER,
    left: contentRect.left < BUFFER,
    right: contentRect.right > window.innerWidth - BUFFER,
  };

  // Check if content is likely in a portal (positioned absolutely and not a child of targetElement)
  const isPortalContent = !targetElement || !targetElement.contains(element);

  // console.log("[isOutOfViewport] Analysis:", {
  //   contentRect: {
  //     top: contentRect.top,
  //     bottom: contentRect.bottom,
  //     left: contentRect.left,
  //     right: contentRect.right,
  //     width: contentRect.width,
  //     height: contentRect.height,
  //   },
  //   viewport: {
  //     innerWidth: window.innerWidth,
  //     innerHeight: window.innerHeight,
  //     heightWithBuffer: window.innerHeight - BUFFER,
  //   },
  //   viewportResult: { ...viewportResult },
  //   isPortalContent,
  //   bufferUsed: BUFFER,
  // });

  // If content is in a portal, only use viewport boundaries
  if (isPortalContent) {
    // console.log(
    //   "[isOutOfViewport] Portal content detected - using viewport only",
    // );
    return viewportResult;
  }

  // For non-portal content, continue with scroll container checks
  const isOutside = { ...viewportResult };

  // Get all scrollable parents
  const getScrollParents = (element: Element | null): Element[] => {
    if (!element) {
      return [];
    }

    const style = window.getComputedStyle(element);
    const isScrollable = /(auto|scroll)/.test(
      style.overflow + style.overflowY + style.overflowX,
    );

    return isScrollable
      ? [element, ...getScrollParents(element.parentElement)]
      : getScrollParents(element.parentElement);
  };

  const scrollParents = targetElement ? getScrollParents(targetElement) : [];

  // console.log(
  //   "[isOutOfViewport] Non-portal content - checking scroll parents:",
  //   scrollParents.length,
  // );

  // Only check scroll containers if they could provide MORE restrictive bounds than viewport
  // and only if the viewport check didn't already flag the content as outside
  scrollParents.forEach((parent) => {
    const parentRect = parent.getBoundingClientRect();

    const parentHeight = parentRect.bottom - parentRect.top;
    const parentWidth = parentRect.right - parentRect.left;

    // console.log(`[isOutOfViewport] Parent ${index}:`, {
    //   tagName: parent.tagName,
    //   className: (parent as HTMLElement).className,
    //   parentRect: {
    //     top: parentRect.top,
    //     bottom: parentRect.bottom,
    //     left: parentRect.left,
    //     right: parentRect.right,
    //     width: parentWidth,
    //     height: parentHeight,
    //   },
    // });

    // Skip small containers or containers that are not significantly smaller than viewport
    const isMainContentArea =
      parentHeight >= window.innerHeight * 0.7 && // At least 70% of viewport height
      parentWidth >= window.innerWidth * 0.5; // At least 50% of viewport width

    if (!isMainContentArea) {
      // console.log(
      //   `[isOutOfViewport] Skipping parent ${index} - not main content area`,
      // );
      return;
    }

    // const beforeCheck = { ...isOutside };

    // Only make restrictions more strict, never more lenient
    // Only check if viewport didn't already mark as outside
    if (!viewportResult.top && contentRect.top < parentRect.top + BUFFER) {
      isOutside.top = true;
    }
    if (
      !viewportResult.bottom &&
      contentRect.bottom > parentRect.bottom - BUFFER
    ) {
      isOutside.bottom = true;
    }
    if (!viewportResult.left && contentRect.left < parentRect.left + BUFFER) {
      isOutside.left = true;
    }
    if (
      !viewportResult.right &&
      contentRect.right > parentRect.right - BUFFER
    ) {
      isOutside.right = true;
    }

    // console.log(`[isOutOfViewport] Parent ${index} applied restrictions:`, {
    //   before: beforeCheck,
    //   after: { ...isOutside },
    // });
  });

  // console.log(
  //   "[isOutOfViewport] Final result for non-portal content:",
  //   isOutside,
  // );

  return isOutside;
};
